import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddMsGroupMembersInput,
  CreateCuProjectInput,
  CreateMsGroupInput,
  MsGroupMembersResult,
  Onboarding,
  OnboardingBackfillResult,
  OnboardingNote,
  OnboardingPhase,
  OnboardingStatus,
  OnboardingStepStatus,
  PaginatedResult,
  SendPmoRequestInput,
  SendSalesRepRequestInput,
  SendWelcomeEmailInput,
  SetOnboardingExceptionInput,
  SortDirection,
  UpdateOnboardingLinksInput,
  WelcomeSectionKey,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface OnboardingListParams {
  page: number;
  pageSize: number;
  customerId?: string;
  /** Narrows to the onboarding record of one opportunity (at most one exists). */
  opportunityId?: string;
  status?: OnboardingStatus;
  /** Multi-select status filter (matches any); wins over `status` when non-empty. */
  statuses?: OnboardingStatus[];
  phase?: OnboardingPhase;
  /** Multi-select phase filter ('none' = no phase set); wins over `phase` when non-empty. */
  phases?: (OnboardingPhase | 'none')[];
  /** Narrows to records flagged (true) or not flagged (false) as an exception. */
  isException?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const onboardingKeys = {
  all: ['onboarding'] as const,
  list: (params: OnboardingListParams) => ['onboarding', 'list', params] as const,
  detail: (id: string) => ['onboarding', 'detail', id] as const,
  notes: (id: string) => ['onboarding', id, 'notes'] as const,
};

export function useOnboardings(
  params: OnboardingListParams,
): UseQueryResult<PaginatedResult<Onboarding>> {
  return useQuery({
    queryKey: onboardingKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Onboarding>>(
        `/onboarding${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          customerId: params.customerId,
          opportunityId: params.opportunityId,
          status: params.status,
          statuses: params.statuses && params.statuses.length > 0 ? params.statuses.join(',') : undefined,
          phase: params.phase,
          phases: params.phases && params.phases.length > 0 ? params.phases.join(',') : undefined,
          isException: params.isException,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useOnboarding(id: string): UseQueryResult<Onboarding> {
  return useQuery({
    queryKey: onboardingKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () => api.get<Onboarding>(`/onboarding/${id}`),
  });
}

export function useCreateOnboarding(): UseMutationResult<Onboarding, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => api.post<Onboarding>('/onboarding', { opportunityId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

/** Admin backfill: onboarding records for all active projects (CW-imported). */
export function useBackfillOnboarding(): UseMutationResult<
  OnboardingBackfillResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<OnboardingBackfillResult>('/onboarding/backfill'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export interface UpdateOnboardingStepArgs {
  id: string;
  stepKey: string;
  status: OnboardingStepStatus;
}

export function useUpdateOnboardingStep(): UseMutationResult<
  Onboarding,
  unknown,
  UpdateOnboardingStepArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stepKey, status }: UpdateOnboardingStepArgs) =>
      api.patch<Onboarding>(`/onboarding/${id}/steps/${stepKey}`, { status }),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface SetOnboardingPhaseArgs {
  id: string;
  phase: OnboardingPhase | null;
}

export function useSetOnboardingPhase(): UseMutationResult<
  Onboarding,
  unknown,
  SetOnboardingPhaseArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, phase }: SetOnboardingPhaseArgs) =>
      api.patch<Onboarding>(`/onboarding/${id}/phase`, { phase }),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface SetOnboardingExceptionArgs {
  id: string;
  input: SetOnboardingExceptionInput;
}

/** Flags (or unflags) the onboarding as an exception, with optional notes. */
export function useSetOnboardingException(): UseMutationResult<
  Onboarding,
  unknown,
  SetOnboardingExceptionArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SetOnboardingExceptionArgs) =>
      api.patch<Onboarding>(`/onboarding/${id}/exception`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface UpdateOnboardingLinksArgs {
  id: string;
  input: UpdateOnboardingLinksInput;
}

/** Manually (re)links the opportunity / project / agreement / CU project (Links tab). */
export function useUpdateOnboardingLinks(): UseMutationResult<
  Onboarding,
  unknown,
  UpdateOnboardingLinksArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateOnboardingLinksArgs) =>
      api.patch<Onboarding>(`/onboarding/${id}/links`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteOnboarding(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/onboarding/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface CreateMsGroupArgs {
  id: string;
  input: CreateMsGroupInput;
}

export function useCreateMsGroup(): UseMutationResult<Onboarding, unknown, CreateMsGroupArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: CreateMsGroupArgs) =>
      api.post<Onboarding>(`/onboarding/${id}/ms-group`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface AddMsGroupMembersArgs {
  id: string;
  input: AddMsGroupMembersInput;
}

export function useAddMsGroupMembers(): UseMutationResult<
  MsGroupMembersResult,
  unknown,
  AddMsGroupMembersArgs
> {
  return useMutation({
    mutationFn: ({ id, input }: AddMsGroupMembersArgs) =>
      api.post<MsGroupMembersResult>(`/onboarding/${id}/ms-group/members`, input),
  });
}

export interface CreateCuProjectArgs {
  id: string;
  input: CreateCuProjectInput;
}

export function useCreateCuProject(): UseMutationResult<Onboarding, unknown, CreateCuProjectArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: CreateCuProjectArgs) =>
      api.post<Onboarding>(`/onboarding/${id}/cu-project`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useRegenerateWelcomeLink(): UseMutationResult<Onboarding, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Onboarding>(`/onboarding/${id}/welcome-link/regenerate`),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useDisableWelcomeLink(): UseMutationResult<Onboarding, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Onboarding>(`/onboarding/${id}/welcome-link/disable`),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface SendWelcomeEmailArgs {
  id: string;
  input: SendWelcomeEmailInput;
}

export function useSendWelcomeEmail(): UseMutationResult<
  Onboarding,
  unknown,
  SendWelcomeEmailArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendWelcomeEmailArgs) =>
      api.post<Onboarding>(`/onboarding/${id}/welcome-email`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useRegenerateSalesRepLink(): UseMutationResult<Onboarding, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Onboarding>(`/onboarding/${id}/sales-rep-link/regenerate`),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface SendSalesRepRequestArgs {
  id: string;
  input: SendSalesRepRequestInput;
}

export function useSendSalesRepRequest(): UseMutationResult<
  Onboarding,
  unknown,
  SendSalesRepRequestArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendSalesRepRequestArgs) =>
      api.post<Onboarding>(`/onboarding/${id}/sales-rep-request`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface SendPmoRequestArgs {
  id: string;
  input: SendPmoRequestInput;
}

/** Sends the Engage PMO email (server-composed body + public PM pre-assign link). */
export function useSendPmoRequest(): UseMutationResult<Onboarding, unknown, SendPmoRequestArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendPmoRequestArgs) =>
      api.post<Onboarding>(`/onboarding/${id}/pmo-request`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface UpdateWelcomeSectionsArgs {
  id: string;
  sections: WelcomeSectionKey[];
}

/** Replaces the set of enabled welcome-form sections. */
export function useUpdateWelcomeSections(): UseMutationResult<
  Onboarding,
  unknown,
  UpdateWelcomeSectionsArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sections }: UpdateWelcomeSectionsArgs) =>
      api.patch<Onboarding>(`/onboarding/${id}/welcome-sections`, { sections }),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export function useOnboardingNotes(onboardingId: string): UseQueryResult<OnboardingNote[]> {
  return useQuery({
    queryKey: onboardingKeys.notes(onboardingId),
    enabled: Boolean(onboardingId),
    queryFn: () => api.get<OnboardingNote[]>(`/onboarding/${onboardingId}/notes`),
  });
}

export interface CreateOnboardingNoteArgs {
  onboardingId: string;
  body: string;
}

export function useCreateOnboardingNote(): UseMutationResult<
  OnboardingNote,
  unknown,
  CreateOnboardingNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ onboardingId, body }: CreateOnboardingNoteArgs) =>
      api.post<OnboardingNote>(`/onboarding/${onboardingId}/notes`, { body }),
    onSuccess: (_data, { onboardingId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.notes(onboardingId) });
    },
  });
}

export interface UpdateOnboardingNoteArgs {
  onboardingId: string;
  noteId: string;
  body: string;
}

export function useUpdateOnboardingNote(): UseMutationResult<
  OnboardingNote,
  unknown,
  UpdateOnboardingNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ onboardingId, noteId, body }: UpdateOnboardingNoteArgs) =>
      api.patch<OnboardingNote>(`/onboarding/${onboardingId}/notes/${noteId}`, { body }),
    onSuccess: (_data, { onboardingId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.notes(onboardingId) });
    },
  });
}

export interface DeleteOnboardingNoteArgs {
  onboardingId: string;
  noteId: string;
}

export function useDeleteOnboardingNote(): UseMutationResult<
  void,
  unknown,
  DeleteOnboardingNoteArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ onboardingId, noteId }: DeleteOnboardingNoteArgs) =>
      api.delete(`/onboarding/${onboardingId}/notes/${noteId}`),
    onSuccess: (_data, { onboardingId }) => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.notes(onboardingId) });
    },
  });
}
